import { Box, HStack, Stack, Text } from "@chakra-ui/react"
import { type FC } from "react"
import type { Address } from "wagmi"

import type { OnDelegateSelected } from "@/governance/components/GovernanceDelegatesExplore"
import type { Organization } from "@/query/graphql/schema"
import EligibleBadge from "@/stake/components/staker/EligibleBadge"
import { useUserDRS } from "@/stake/hooks/useUserDRS"

export enum DelegationType {
  SELF = "self",
  CUSTOM = "custom",
}

export type DelegateeTypeSelectorProps = {
  setDelegatee?: OnDelegateSelected
  setDelegateeType: (delegateeType: DelegationType | null) => void
  meAddress: Address
  delegateeType: DelegationType | null
  organization: Organization
}

const DelegateeTypeSelector: FC<DelegateeTypeSelectorProps> = ({
  setDelegatee,
  setDelegateeType,
  delegateeType,
  meAddress,
  organization,
}) => {
  const { isDRSElegible, isDRSActive } = useUserDRS({
    address: meAddress,
    organization,
  })
  const options = [
    {
      label: "Myself",
      description: "You will vote directly on proposals",
      value: DelegationType.SELF,
      onClick: () => {
        setDelegatee && setDelegatee(meAddress)
        setDelegateeType(DelegationType.SELF)
      },
      isDisabled: isDRSActive && !isDRSElegible,
    },
    {
      label: "Someone else",
      description: "Another address will vote on your behalf",
      value: DelegationType.CUSTOM,
      onClick: () => {
        setDelegateeType(DelegationType.CUSTOM)
      },
    },
  ]

  return (
    <Stack spacing={4}>
      {options.map(({ value, onClick, label, description, isDisabled }) => (
        <Box
          key={value}
          borderColor={delegateeType === value ? "gray.900" : "gray.200"}
          borderRadius="md"
          borderWidth={1}
          cursor={isDisabled ? "unset" : "pointer"}
          opacity={isDisabled ? 0.5 : 1}
          p={4}
          onClick={isDisabled ? undefined : onClick}
        >
          <HStack>
            <Text fontSize="xl" fontWeight="semibold">
              {label}
            </Text>
            {isDisabled && (
              <EligibleBadge useV2IneligibleBadge isEligible={false} />
            )}
          </HStack>

          <Text color="gray.600">{description}</Text>
        </Box>
      ))}
    </Stack>
  )
}

export default DelegateeTypeSelector
